import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  CircularProgress,
  Paper,
} from '@mui/material';
import imageCompression from 'browser-image-compression';
import PhotoCamera from '@mui/icons-material/PhotoCamera';
import AddLocationIcon from '@mui/icons-material/AddLocation';
import EditIcon from '@mui/icons-material/Edit';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import MapComponent from '../../components/Map/MapComponent';
import { getLocationHierarchy } from '../../utils';
import { styles } from './LocationDetails.styles';
import './LocationDetails.css';
import DisplayJSON from '../../components/DisplayJSON/DisplayJSON';
import { 
  useUpdateLocationMutation,
  useUpdatePlaceMutation,
  useDeleteLocationMutation,
  useUploadLocationPhotoMutation,
  useGetUsersQuery
} from '../../redux/adminApiSlice';
import { convertToProxyUrl } from '../../utils/photoProxy';

// Location `level` may be stored TITLE-CASE ("Country", "Neighborhood") while
// place nodes are tagged UPPERCASE ("PLACE"). The <Select> below uses title-case
// option values, so normalize any level to Title Case for the control value to
// avoid MUI "out-of-range value" warnings and mismatched comparisons.
const LEVEL_OPTIONS = [
  'Place',
  'Global',
  'Region',
  'Country',
  'State',
  'City',
  'Neighborhood',
] as const;

const toTitleCaseLevel = (level: string | undefined | null): string => {
  if (!level) return '';
  const match = LEVEL_OPTIONS.find((opt) => opt.toUpperCase() === String(level).toUpperCase());
  return match ?? String(level);
};

const isPlaceLevel = (level: string | undefined | null): boolean =>
  String(level ?? '').toUpperCase() === 'PLACE';

interface LocationDetailsProps {
  location: any;
  cornerMarkers: any;
  handleMarkerDrag: any;
  visibleNeighborhoods: any;
  handleSetCenterPoint: any;
  handleSaveBoundingBox: any;
  fetchGoogleBounds: any;
  calculateCenterFromBoundingBox: any;
  setShowCenterPointDialog: any;
  hasUnsavedChanges: boolean;
  setHasUnsavedChanges: (value: boolean) => void;
  getLocationIssues: any;
  locationTree: any;
  onLocationUpdated?: () => void;
}

const LocationDetails: React.FC<LocationDetailsProps> = React.memo(({
  location: rawLocation,
  cornerMarkers,
  handleMarkerDrag,
  visibleNeighborhoods,
  handleSetCenterPoint,
  handleSaveBoundingBox,
  fetchGoogleBounds,
  calculateCenterFromBoundingBox,
  setShowCenterPointDialog,
  hasUnsavedChanges,
  setHasUnsavedChanges,
  getLocationIssues,
  locationTree,
  onLocationUpdated
}) => {
  // The tree tags place nodes with an UPPERCASE level ("PLACE") while locations
  // are stored title-case ("Country", "Neighborhood"). Normalize the level to
  // Title Case so the many `location.level === 'Place' | 'Country'` comparisons
  // (and the level <Select>) below work regardless of the source casing.
  const location = useMemo(
    () => ({ ...rawLocation, level: toTitleCaseLevel(rawLocation?.level) }),
    [rawLocation]
  );
  const [editedLocation, setEditedLocation] = useState(() => ({
    ...rawLocation,
    level: toTitleCaseLevel(rawLocation?.level),
  }));
  const [error, setError] = useState<string | null>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [localCornerMarkers, setLocalCornerMarkers] = useState<{[key: string]: google.maps.LatLngLiteral}>({});
  const [editMode, setEditMode] = useState<'move' | 'add'>('move');
  const [polygonPoints, setPolygonPoints] = useState<google.maps.LatLngLiteral[]>([]);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deletingChildren, setDeletingChildren] = useState<{ [key: string]: boolean }>({});
  const [errorChildren, setErrorChildren] = useState<Array<{ id: string; name: string; level: string }>>([]);
  const [showJsonDialog, setShowJsonDialog] = useState(false);
  const [isUploadingPhoto, setIsUploadingPhoto] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<string>('');
  const [uploadSuccess, setUploadSuccess] = useState(false);
  const [isDragOver, setIsDragOver] = useState(false);
  const [isEditingBounds, setIsEditingBounds] = useState(false);

  // Replace axios calls with RTK Query hooks
  const [updateLocation] = useUpdateLocationMutation();
  const [updatePlace] = useUpdatePlaceMutation();
  const [deleteLocation] = useDeleteLocationMutation();
  const [uploadLocationPhoto] = useUploadLocationPhotoMutation();
  const { data: users = [], isLoading: loadingUsers } = useGetUsersQuery();

  // Memoize bounds calculation to prevent expensive recalculation
  const bounds = useMemo(() => {
    if (polygonPoints.length >= 3) {
      const lats = polygonPoints.map(p => p.lat);
      const lngs = polygonPoints.map(p => p.lng);
      return {
        north: Math.max(...lats),
        south: Math.min(...lats),
        east: Math.max(...lngs),
        west: Math.min(...lngs)
      };
    } else if (location.boundingBox?.coordinates?.[0]) {
      const coords = location.boundingBox.coordinates[0];
      const lats = coords.map(([_, lat]: [number, number]) => lat);
      const lngs = coords.map(([lng, _]: [number, number]) => lng);
      return {
        north: Math.max(...lats),
        south: Math.min(...lats),
        east: Math.max(...lngs),
        west: Math.min(...lngs)
      };
    }
    return null;
  }, [polygonPoints, location.boundingBox]);

  // Memoize map bounds calculation - don't update during bounding box editing
  const mapBounds = useMemo(() => {
    if (!bounds || isEditingBounds) return null;
    const latSpan = bounds.north - bounds.south;
    const lngSpan = bounds.east - bounds.west;
    return {
      north: bounds.north + latSpan * 0.1,
      south: bounds.south - latSpan * 0.1,
      east: bounds.east + lngSpan * 0.1,
      west: bounds.west - lngSpan * 0.1
    };
  }, [bounds, isEditingBounds]);

  const hasCoordinates = location.coordinates?.coordinates && 
    Array.isArray(location.coordinates.coordinates) && 
    location.coordinates.coordinates.length >= 2;

  const handlePlaceLocationChange = useCallback(async (position: google.maps.LatLngLiteral) => {
    if (location.level !== 'Place') return;
    
    setEditedLocation((prev: any) => ({
      ...prev,
      location: {
        type: 'Point',
        coordinates: [position.lng, position.lat]
      }
    }));
    setHasUnsavedChanges(true);
  }, [location.level, setHasUnsavedChanges]);

  const getPlaceLocation = useCallback((): google.maps.LatLngLiteral | undefined => {
    if (location.level !== 'Place' || !location.location?.coordinates) return undefined;
    return {
      lat: location.location.coordinates[1],
      lng: location.location.coordinates[0]
    };
  }, [location.level, location.location?.coordinates]);

  // Memoize map center calculation
  const mapCenter = useMemo(() => {
    if (location.level === 'Place') {
      return getPlaceLocation() || { lat: 40.7831, lng: -73.9712 }; // Default to Manhattan if no location
    } else if (bounds) {
      return {
        lat: (bounds.north + bounds.south) / 2,
        lng: (bounds.east + bounds.west) / 2
      };
    } else if (hasCoordinates) {
      return {
        lat: location.coordinates.coordinates[1],
        lng: location.coordinates.coordinates[0]
      };
    }
    return null;
  }, [location.level, getPlaceLocation, bounds, hasCoordinates, location.coordinates]);

  useEffect(() => {
    console.log('Location changed or reset requested:', {
      locationId: location._id,
      boundingBox: location.boundingBox
    });
    
    // Reset polygon points when location changes
    if (location.boundingBox?.coordinates?.[0]) {
      const points = location.boundingBox.coordinates[0].slice(0, -1).map(([lng, lat]: [number, number]) => ({
        lat,
        lng
      }));
      console.log('Setting polygon points for location:', {
        locationId: location._id,
        points
      });
      setPolygonPoints(points);
    } else {
      setPolygonPoints([]);
    }

    // Set edited location with the correct parent ID format
    setEditedLocation({
      ...location,
      parent: typeof location.parent === 'object' ? location.parent?._id : location.parent
    });
  }, [location]); // Watch the entire location object for changes

  // Update edited location when points change
  useEffect(() => {
    if (polygonPoints.length >= 3) {
      // Create a copy of points and ensure it's closed
      const closedPoints = [...polygonPoints];
      if (JSON.stringify(closedPoints[0]) !== JSON.stringify(closedPoints[closedPoints.length - 1])) {
        closedPoints.push({ ...closedPoints[0] });
      }

      const boundingBox = {
        type: 'Polygon' as const,
        coordinates: [[
          ...closedPoints.map(point => [point.lng, point.lat])
        ]]
      };

      setEditedLocation((prev: typeof location) => ({
        ...prev,
        boundingBox
      }));
      setHasUnsavedChanges(true);
    }
  }, [polygonPoints, setHasUnsavedChanges]);

  // Update local corner markers when bounding box changes
  useEffect(() => {
    if (editedLocation.boundingBox?.coordinates?.[0]) {
      const coords = editedLocation.boundingBox.coordinates[0];
      setLocalCornerMarkers({
        nw: { lat: coords[0][1], lng: coords[0][0] },
        ne: { lat: coords[1][1], lng: coords[1][0] },
        se: { lat: coords[2][1], lng: coords[2][0] },
        sw: { lat: coords[3][1], lng: coords[3][0] }
      });
    } else {
      setLocalCornerMarkers({});
    }
  }, [editedLocation.boundingBox]);

  const handleAddPoint = useCallback((point: google.maps.LatLngLiteral) => {
    console.log('Adding point:', point);
    const newPoints = [...polygonPoints];
    
    // If this is the first point, or if we're closing the polygon
    if (newPoints.length === 0 || 
        (newPoints.length >= 3 && 
         Math.abs(point.lat - newPoints[0].lat) < 0.0001 && 
         Math.abs(point.lng - newPoints[0].lng) < 0.0001)) {
      if (newPoints.length >= 3) {
        // Close the polygon by adding the first point again
        newPoints.push({ ...newPoints[0] });
      } else {
        newPoints.push(point);
      }
    } else {
      newPoints.push(point);
    }
    
    console.log('Updated points array:', newPoints);
    setPolygonPoints(newPoints);
    setHasUnsavedChanges(true);

    // Update edited location's bounding box immediately
    if (newPoints.length >= 3) {
      const boundingBox = {
        type: 'Polygon' as const,
        coordinates: [[
          ...newPoints.map(p => [p.lng, p.lat]),
          // Close the polygon if it's not already closed
          newPoints.length >= 3 && JSON.stringify(newPoints[0]) !== JSON.stringify(newPoints[newPoints.length - 1]) 
            ? [newPoints[0].lng, newPoints[0].lat] 
            : undefined
        ].filter(Boolean)]
      };
      setEditedLocation((prev: typeof location) => ({
        ...prev,
        boundingBox
      }));
    }
  }, [polygonPoints, setHasUnsavedChanges]);

  const handleDeletePoint = (index: number) => {
    console.log('Deleting point at index:', index);
    const newPoints = [...polygonPoints];
    newPoints.splice(index, 1);
    
    // If we deleted the last point (which should match the first point),
    // update the last point to match the new first point
    if (index === newPoints.length && newPoints.length >= 3) {
      newPoints[newPoints.length - 1] = { ...newPoints[0] };
    }
    
    console.log('Updated points array after deletion:', newPoints);
    setPolygonPoints(newPoints);
    setHasUnsavedChanges(true);

    // Update edited location's bounding box immediately
    if (newPoints.length >= 3) {
      const boundingBox = {
        type: 'Polygon' as const,
        coordinates: [[
          ...newPoints.map(p => [p.lng, p.lat]),
          // Close the polygon if it's not already closed
          JSON.stringify(newPoints[0]) !== JSON.stringify(newPoints[newPoints.length - 1]) 
            ? [newPoints[0].lng, newPoints[0].lat] 
            : undefined
        ].filter(Boolean)]
      };
      setEditedLocation((prev: typeof location) => ({
        ...prev,
        boundingBox
      }));
    }
  };

  const handleInsertPoint = (point: google.maps.LatLngLiteral, index: number) => {
    console.log('Inserting point:', point, 'at index:', index);
    const newPoints = [...polygonPoints];
    newPoints.splice(index, 0, point);
    console.log('Updated points array after insertion:', newPoints);
    setPolygonPoints(newPoints);
    setHasUnsavedChanges(true);

    // Update edited location's bounding box immediately
    if (newPoints.length >= 3) {
      const boundingBox = {
        type: 'Polygon' as const,
        coordinates: [[
          ...newPoints.map(p => [p.lng, p.lat]),
          // Close the polygon if it's not already closed
          JSON.stringify(newPoints[0]) !== JSON.stringify(newPoints[newPoints.length - 1]) 
            ? [newPoints[0].lng, newPoints[0].lat] 
            : undefined
        ].filter(Boolean)]
      };
      setEditedLocation((prev: typeof location) => ({
        ...prev,
        boundingBox
      }));
    }
  };

  const handleMarkerDragLocal = useCallback((position: google.maps.LatLngLiteral, markerId: string) => {
    console.log('Marker drag:', { position, markerId });
    const index = parseInt(markerId.split('-')[1]);
    
    if (isNaN(index) || index < 0 || index >= polygonPoints.length) {
      console.error('Invalid marker index:', index);
      return;
    }

    const newPoints = [...polygonPoints];
    newPoints[index] = position;
    
    // If we moved the first point (index 0), update the last point to match
    // Only do this if we have a closed polygon (first and last points match)
    if (index === 0 && newPoints.length >= 3 && 
        JSON.stringify(polygonPoints[0]) === JSON.stringify(polygonPoints[polygonPoints.length - 1])) {
      newPoints[newPoints.length - 1] = { ...position };
    }
    
    // If we moved the last point and it was part of a closed polygon, update the first point
    if (index === newPoints.length - 1 && newPoints.length >= 3 && 
        JSON.stringify(polygonPoints[0]) === JSON.stringify(polygonPoints[polygonPoints.length - 1])) {
      newPoints[0] = { ...position };
    }
    
    console.log('Updated points array after drag:', newPoints);
    setPolygonPoints(newPoints);
    setHasUnsavedChanges(true);

    // Update edited location's bounding box immediately
    if (newPoints.length >= 3) {
      const boundingBox = {
        type: 'Polygon' as const,
        coordinates: [[
          ...newPoints.map(p => [p.lng, p.lat]),
          // Close the polygon if it's not already closed
          JSON.stringify(newPoints[0]) !== JSON.stringify(newPoints[newPoints.length - 1]) 
            ? [newPoints[0].lng, newPoints[0].lat] 
            : undefined
        ].filter(Boolean)]
      };
      setEditedLocation((prev: typeof location) => ({
        ...prev,
        boundingBox
      }));
    }
  }, [polygonPoints, setHasUnsavedChanges]);

  // Custom drag handlers that manage editing state
  const handleMarkerDragStart = useCallback(() => {
    setIsEditingBounds(true);
  }, []);

  const handleMarkerDragEnd = useCallback((position: google.maps.LatLngLiteral, markerId: string) => {
    handleMarkerDragLocal(position, markerId);
    setIsEditingBounds(false);
  }, [handleMarkerDragLocal]);

  const handlePhotoChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    if (!event.target.files?.length) return;
    
    const file = event.target.files[0];
    await processPhotoUpload(file);
  };

  const processPhotoUpload = async (file: File) => {
    // Validate file size (max 10MB before compression)
    const maxSize = 10 * 1024 * 1024; // 10MB
    if (file.size > maxSize) {
      setError('File size must be less than 10MB');
      return;
    }
    
    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif'];
    if (!allowedTypes.includes(file.type)) {
      setError('Please select a valid image file (JPEG, PNG, or GIF)');
      return;
    }
    
    try {
      setIsUploadingPhoto(true);
      setUploadProgress('Preparing upload...');
      setError(null);
      setUploadSuccess(false);
      
      // Compress the image
      setUploadProgress('Compressing image...');
      const compressionOptions = {
        maxSizeMB: 2, // Maximum file size after compression
        maxWidthOrHeight: 1920, // Maximum width or height
        useWebWorker: true, // Use web worker for better performance
        quality: 0.8, // JPEG quality (0.1 - 1.0)
      };
      
      console.log('[LocationDetails] Original file size:', (file.size / 1024 / 1024).toFixed(2), 'MB');
      
      const compressedFile = await imageCompression(file, compressionOptions);
      
      console.log('[LocationDetails] Compressed file size:', (compressedFile.size / 1024 / 1024).toFixed(2), 'MB');
      console.log('[LocationDetails] Compression ratio:', ((1 - compressedFile.size / file.size) * 100).toFixed(1) + '%');
      
      // Simulate progress steps for better UX
      setTimeout(() => setUploadProgress('Uploading to S3...'), 500);
      
      const response = await uploadLocationPhoto({ photo: compressedFile, locationId: location._id });
      
      setUploadProgress('Updating location...');
      
      if ('data' in response && response.data?.photoUrl) {
        setEditedLocation({
          ...editedLocation,
          photo: response.data.photoUrl
        });

        setUploadProgress('Upload complete!');
        setUploadSuccess(true);
        
        // Clear success message after 3 seconds
        setTimeout(() => {
          setUploadProgress('');
          setUploadSuccess(false);
        }, 3000);

        if (onLocationUpdated) {
          onLocationUpdated();
        }
      }
    } catch (err: any) {
      console.error('Photo upload error:', err);
      const errorMessage = err?.data?.message || err?.message || 'Failed to upload photo';
      setError(errorMessage);
      setUploadProgress('');
      setUploadSuccess(false);
    } finally {
      setIsUploadingPhoto(false);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      await processPhotoUpload(files[0]);
    }
  };

  const handleSave = async () => {
    try {
      if (location.level === 'Place') {
        const updateData = {
          name: editedLocation.name,
          description: editedLocation.description,
          type: editedLocation.type,
          status: editedLocation.status,
          photo: editedLocation.photo,
          address: editedLocation.address,
          location: editedLocation.location || {
            type: 'Point',
            coordinates: [0, 0]
          },
          neighborhood: editedLocation.parent || null,
          creator: editedLocation.creator,
          joinedUsers: editedLocation.joinedUsers || [],
          currentUsers: editedLocation.currentUsers || [],
          onlineUsers: editedLocation.onlineUsers || [],
          userCount: editedLocation.userCount || 0,
          joinRequests: editedLocation.joinRequests || [],
          invitations: editedLocation.invitations || []
        };

        const response = await updatePlace({ id: location._id, updates: updateData });
        if (response.data) {
          const updatedLocation = {
            ...response.data,
            parent: typeof response.data.parent === 'object' ? response.data.parent?._id : response.data.parent
          };
          setEditedLocation(updatedLocation);
          setHasUnsavedChanges(false);
          setIsEditing(false);
          
          if (onLocationUpdated) {
            onLocationUpdated();
          }
        }
      } else {
        const updateData = {
          name: editedLocation.name,
          level: editedLocation.level,
          parent: editedLocation.parent || null,
          totalUsers: parseInt(editedLocation.totalUsers?.toString() || '0')
        };

        const response = await updateLocation({ id: location._id, updates: updateData });
        if (response.data) {
          const updatedLocation = {
            ...response.data.location,
            parent: typeof response.data.location.parent === 'object' ? response.data.location.parent?._id : response.data.location.parent
          };
          setEditedLocation(updatedLocation);
          setHasUnsavedChanges(false);
          setIsEditing(false);
          
          if (onLocationUpdated) {
            onLocationUpdated();
          }
        }
      }
    } catch (err: any) {
      console.error('Error updating location:', err);
      setError(err.message || 'Failed to update location');
    }
  };

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      setDeleteError(null);
      setErrorChildren([]);
      
      const response = await deleteLocation({ id: location._id, level: location.level });
      
      if ('data' in response) {
        setShowDeleteDialog(false);
        if (onLocationUpdated) {
          onLocationUpdated();
        }
      } else if ('error' in response) {
        console.error('Error deleting location:', response.error);
        // Handle both FetchBaseQueryError and SerializedError types
        const errorData = 'status' in response.error ? 
          (response.error.data as { message?: string })?.message : 
          (response.error as { message?: string })?.message;
        const errorMessage = errorData || 'Failed to delete location';
        setDeleteError(errorMessage);
        
        try {
          if (errorMessage.includes('Found') && errorMessage.includes('children:')) {
            const jsonStr = errorMessage.split('children:')[1].split('Please')[0].trim();
            const children = JSON.parse(jsonStr);
            setErrorChildren(children);
          }
        } catch (parseError) {
          console.error('Error parsing children from error message:', parseError);
        }
      }
    } catch (error: any) {
      console.error('Error deleting location:', error);
      setDeleteError(error.message || 'Failed to delete location');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleDeleteChild = async (childId: string, childName: string, childLevel: string) => {
    try {
      setDeletingChildren(prev => ({ ...prev, [childId]: true }));
      
      await deleteLocation({ id: childId, level: childLevel });
      
      if (onLocationUpdated) {
        onLocationUpdated();
      }
    } catch (error: any) {
      console.error(`Error deleting child location ${childName}:`, error);
      setDeleteError(`Failed to delete ${childName}: ${error.message || 'Unknown error'}`);
    } finally {
      setDeletingChildren(prev => ({ ...prev, [childId]: false }));
    }
  };

  return (
    <Box sx={styles.container}>
      <Box sx={styles.contentSection}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            {location.name} ({location.level})
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => setShowJsonDialog(true)}
              startIcon={<InfoIcon />}
            >
              View JSON
            </Button>
            <Button
              variant="outlined"
              color="error"
              startIcon={<DeleteIcon />}
              onClick={() => setShowDeleteDialog(true)}
              disabled={location.level === 'Country'}
            >
              Delete
            </Button>
          </Box>
        </Box>

        {/* Location Hierarchy */}
        <Box sx={styles.locationInfo}>
          <Typography variant="h5" gutterBottom>
            {location.name}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {locationTree && getLocationHierarchy(location, locationTree).split(' / ').slice(1).join(' / ')}
          </Typography>
          <Typography variant="body2" sx={{ mt: 1 }}>
            Level: {location.level}  ID: {location._id} • Parent ID: {typeof location.parent === 'string' ? location.parent : location.parent?._id || 'None'}
          </Typography>
        </Box>

        {/* Stats Display */}
        <Box sx={styles.statsDisplay}>
          <Typography>
            Users: Online: {location.onlineUsers || 0} / Total: {location.totalUsers || 0}
          </Typography>
          <Typography>
            Posts: {location.totalPosts || 0}
          </Typography>
        </Box>

        {/* Location Details Form */}
        <Box sx={styles.locationForm}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {/* Common Fields for both Places and Locations */}
          <TextField
            fullWidth
            label="Name"
            value={editedLocation.name}
            onChange={(e) => setEditedLocation({ ...editedLocation, name: e.target.value })}
            disabled={!isEditing}
            sx={styles.formField}
          />

          <FormControl fullWidth sx={styles.formField}>
            <InputLabel>Level</InputLabel>
            <Select
              value={toTitleCaseLevel(editedLocation.level)}
              label="Level"
              onChange={(e) => setEditedLocation({ ...editedLocation, level: e.target.value })}
              disabled={!isEditing || isPlaceLevel(location.level)}
            >
              <MenuItem value="Place">Place</MenuItem>
              <MenuItem value="Global">Global</MenuItem>
              <MenuItem value="Region">Region</MenuItem>
              <MenuItem value="Country">Country</MenuItem>
              <MenuItem value="State">State</MenuItem>
              <MenuItem value="City">City</MenuItem>
              <MenuItem value="Neighborhood">Neighborhood</MenuItem>
            </Select>
          </FormControl>

          <FormControl fullWidth sx={styles.formField}>
            <InputLabel>Parent Location</InputLabel>
            <Select
              value={editedLocation.parent || ''}
              label="Parent Location"
              onChange={(e) => setEditedLocation({ ...editedLocation, parent: e.target.value })}
              disabled={!isEditing || editedLocation.level === 'Country'}
            >
              <MenuItem value="">None</MenuItem>
              {locationTree.flatMap((node: any) => {
                const flatten = (node: any): any[] => {
                  if (!node || node.level === 'Place') return [node];
                  return [node, ...node.children.flatMap(flatten)];
                };
                return flatten(node);
              })
                .filter((loc: any) => {
                  if (editedLocation.level === 'Place') return loc.level === 'Neighborhood';
                  if (editedLocation.level === 'Neighborhood') return loc.level === 'City';
                  if (editedLocation.level === 'City') return loc.level === 'State';
                  if (editedLocation.level === 'State') return loc.level === 'Country';
                  return false;
                })
                .map((loc: any) => (
                  <MenuItem key={loc._id} value={loc._id}>
                    {loc.name} ({loc.level})
                  </MenuItem>
                ))}
            </Select>
          </FormControl>

          {/* Photo Upload - Available for both Places and Locations */}
          <Box sx={styles.photoContainer}>
            <input
              accept="image/*"
              className="hidden-input"
              id="photo-upload"
              type="file"
              onChange={handlePhotoChange}
              disabled={isUploadingPhoto}
              style={{ display: 'none' }}
            />
            
            {/* Photo Display or Upload Area */}
            <Box
              sx={{
                width: 200,
                height: 150,
                border: `2px dashed ${isDragOver ? '#1976d2' : location.photo ? 'transparent' : '#ccc'}`,
                borderRadius: 2,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                backgroundColor: isDragOver ? 'rgba(0, 229, 255, 0.08)' : location.photo ? 'transparent' : 'action.hover',
                transition: 'all 0.2s ease',
                cursor: isUploadingPhoto ? 'not-allowed' : 'pointer',
                opacity: isUploadingPhoto ? 0.6 : 1,
                position: 'relative',
                overflow: 'hidden'
              }}
              onDragOver={handleDragOver}
              onDragLeave={handleDragLeave}
              onDrop={handleDrop}
              onClick={() => !isUploadingPhoto && document.getElementById('photo-upload')?.click()}
            >
              {location.photo && !isUploadingPhoto ? (
                <>
                  <img
                    src={convertToProxyUrl(location.photo) || location.photo}
                    alt="Location"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      borderRadius: '6px'
                    }}
                  />
                  <Box
                    sx={{
                      position: 'absolute',
                      top: 0,
                      left: 0,
                      right: 0,
                      bottom: 0,
                      backgroundColor: 'rgba(0, 0, 0, 0.5)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      opacity: 0,
                      transition: 'opacity 0.2s ease',
                      '&:hover': {
                        opacity: 1
                      }
                    }}
                  >
                    <Typography variant="body2" sx={{ color: 'white', textAlign: 'center' }}>
                      Click to change photo
                    </Typography>
                  </Box>
                </>
              ) : (
                <>
                  {isUploadingPhoto ? (
                    <CircularProgress size={40} />
                  ) : (
                    <PhotoCamera sx={{ fontSize: 40, color: '#999', mb: 1 }} />
                  )}
                  
                  <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', px: 1 }}>
                    {isUploadingPhoto 
                      ? 'Uploading...' 
                      : isDragOver 
                        ? 'Drop image here'
                        : 'Click to upload photo'
                    }
                  </Typography>
                  
                  {!isUploadingPhoto && (
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'center', px: 1 }}>
                      JPEG, PNG, GIF (max 10MB)
                    </Typography>
                  )}
                </>
              )}
            </Box>
          </Box>

          {/* Progress and Status Messages */}
          {isUploadingPhoto && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={16} />
              <Typography variant="body2" color="text.secondary">
                {uploadProgress || 'Uploading photo...'}
              </Typography>
            </Box>
          )}
          
          {uploadSuccess && !isUploadingPhoto && (
            <Box sx={{ mt: 1, display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box sx={{ 
                width: 16, 
                height: 16, 
                borderRadius: '50%', 
                backgroundColor: 'success.main',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <Typography sx={{ color: 'white', fontSize: '10px' }}>✓</Typography>
              </Box>
              <Typography variant="body2" color="success.main">
                Photo uploaded successfully!
              </Typography>
            </Box>
          )}
          
          {error && error.includes('photo') && (
            <Alert severity="error" sx={{ mt: 1 }}>
              {error}
            </Alert>
          )}

          {/* Fields specific to Locations (non-Places) */}
          {location.level !== 'Place' && (
            <>
              <TextField
                fullWidth
                label="Population"
                type="number"
                value={editedLocation.population || 0}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (!isNaN(value) && value >= 0) {
                    setEditedLocation({ ...editedLocation, population: value });
                  }
                }}
                disabled={!isEditing}
                sx={styles.formField}
              />

              <FormControl fullWidth sx={styles.formField}>
                <InputLabel>Is Town</InputLabel>
                <Select
                  value={editedLocation.isTown ? "true" : "false"}
                  label="Is Town"
                  onChange={(e) => setEditedLocation({ ...editedLocation, isTown: e.target.value === "true" })}
                  disabled={!isEditing}
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Total Posts"
                  type="number"
                  value={editedLocation.totalPosts || 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 0) {
                      setEditedLocation({ ...editedLocation, totalPosts: value });
                    }
                  }}
                  disabled={!isEditing}
                  sx={styles.formField}
                />

                <TextField
                  fullWidth
                  label="Total Promotes"
                  type="number"
                  value={editedLocation.totalPromotes || 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 0) {
                      setEditedLocation({ ...editedLocation, totalPromotes: value });
                    }
                  }}
                  disabled={!isEditing}
                  sx={styles.formField}
                />
              </Box>

              <TextField
                fullWidth
                label="Average Promotes Per Post"
                type="number"
                value={editedLocation.averagePromotesPerPost || 0}
                onChange={(e) => {
                  const value = parseFloat(e.target.value);
                  if (!isNaN(value) && value >= 0) {
                    setEditedLocation({ ...editedLocation, averagePromotesPerPost: value });
                  }
                }}
                disabled={!isEditing}
                sx={styles.formField}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Online Users"
                  type="number"
                  value={editedLocation.onlineUsers || 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 0) {
                      setEditedLocation({ ...editedLocation, onlineUsers: value });
                    }
                  }}
                  disabled={!isEditing}
                  sx={styles.formField}
                />

                <TextField
                  fullWidth
                  label="Total Users"
                  type="number"
                  value={editedLocation.totalUsers || 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 0) {
                      setEditedLocation({ ...editedLocation, totalUsers: value });
                    }
                  }}
                  disabled={!isEditing}
                  sx={styles.formField}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Latitude"
                  type="number"
                  value={editedLocation.coordinates?.coordinates[1] || ''}
                  onChange={(e) => {
                    const lat = parseFloat(e.target.value);
                    if (!isNaN(lat)) {
                      setEditedLocation({
                        ...editedLocation,
                        coordinates: {
                          type: 'Point',
                          coordinates: [editedLocation.coordinates?.coordinates[0] || 0, lat]
                        }
                      });
                    }
                  }}
                  disabled={!isEditing}
                  sx={styles.formField}
                />
                <TextField
                  fullWidth
                  label="Longitude"
                  type="number"
                  value={editedLocation.coordinates?.coordinates[0] || ''}
                  onChange={(e) => {
                    const lng = parseFloat(e.target.value);
                    if (!isNaN(lng)) {
                      setEditedLocation({
                        ...editedLocation,
                        coordinates: {
                          type: 'Point',
                          coordinates: [lng, editedLocation.coordinates?.coordinates[1] || 0]
                        }
                      });
                    }
                  }}
                  disabled={!isEditing}
                  sx={styles.formField}
                />
              </Box>
            </>
          )}

          {/* Fields specific to Places */}
          {location.level === 'Place' && (
            <>
              <TextField
                fullWidth
                label="Description"
                multiline
                rows={3}
                value={editedLocation.description || ''}
                onChange={(e) => setEditedLocation({ ...editedLocation, description: e.target.value })}
                disabled={!isEditing}
                sx={styles.formField}
              />

              <FormControl fullWidth sx={styles.formField}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={editedLocation.type || ''}
                  label="Type"
                  onChange={(e) => setEditedLocation({ ...editedLocation, type: e.target.value })}
                  disabled={!isEditing}
                >
                  <MenuItem value="restaurant">Restaurant</MenuItem>
                  <MenuItem value="bar">Bar</MenuItem>
                  <MenuItem value="club">Club</MenuItem>
                  <MenuItem value="cafe">Cafe</MenuItem>
                  <MenuItem value="park">Park</MenuItem>
                  <MenuItem value="gym">Gym</MenuItem>
                  <MenuItem value="retail">Retail</MenuItem>
                  <MenuItem value="entertainment">Entertainment</MenuItem>
                  <MenuItem value="other">Other</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={styles.formField}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={editedLocation.status || 'public'}
                  label="Status"
                  onChange={(e) => setEditedLocation({ ...editedLocation, status: e.target.value })}
                  disabled={!isEditing}
                >
                  <MenuItem value="public">Public</MenuItem>
                  <MenuItem value="private">Private</MenuItem>
                  <MenuItem value="invite-only">Invite Only</MenuItem>
                </Select>
              </FormControl>

              <TextField
                fullWidth
                label="Address"
                value={editedLocation.address || ''}
                onChange={(e) => setEditedLocation({ ...editedLocation, address: e.target.value })}
                disabled={!isEditing}
                sx={styles.formField}
              />

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="Latitude"
                  type="number"
                  value={editedLocation.location?.coordinates[1] || ''}
                  onChange={(e) => {
                    const lat = parseFloat(e.target.value);
                    if (!isNaN(lat)) {
                      setEditedLocation({
                        ...editedLocation,
                        location: {
                          type: 'Point',
                          coordinates: [editedLocation.location?.coordinates[0] || 0, lat]
                        }
                      });
                    }
                  }}
                  disabled={!isEditing}
                  sx={styles.formField}
                />
                <TextField
                  fullWidth
                  label="Longitude"
                  type="number"
                  value={editedLocation.location?.coordinates[0] || ''}
                  onChange={(e) => {
                    const lng = parseFloat(e.target.value);
                    if (!isNaN(lng)) {
                      setEditedLocation({
                        ...editedLocation,
                        location: {
                          type: 'Point',
                          coordinates: [lng, editedLocation.location?.coordinates[1] || 0]
                        }
                      });
                    }
                  }}
                  disabled={!isEditing}
                  sx={styles.formField}
                />
              </Box>

              <Box sx={{ display: 'flex', gap: 2 }}>
                <TextField
                  fullWidth
                  label="User Count"
                  type="number"
                  value={editedLocation.userCount || 0}
                  onChange={(e) => {
                    const value = parseInt(e.target.value);
                    if (!isNaN(value) && value >= 0) {
                      setEditedLocation({ ...editedLocation, userCount: value });
                    }
                  }}
                  disabled={!isEditing}
                  sx={styles.formField}
                />
                <TextField
                  fullWidth
                  label="Online Users"
                  type="number"
                  value={editedLocation.onlineUsers?.length || 0}
                  disabled
                  sx={styles.formField}
                />
              </Box>

              <TextField
                fullWidth
                label="Entry Radius (feet)"
                type="number"
                value={editedLocation.entryRadiusFeet || ''}
                onChange={(e) => {
                  const value = parseInt(e.target.value);
                  if (!isNaN(value) && value >= 0) {
                    setEditedLocation({ ...editedLocation, entryRadiusFeet: value });
                  }
                }}
                disabled={!isEditing}
                sx={styles.formField}
              />

              <FormControl fullWidth sx={styles.formField}>
                <InputLabel>Is Official</InputLabel>
                <Select
                  value={editedLocation.isOfficial ? "true" : "false"}
                  label="Is Official"
                  onChange={(e) => setEditedLocation({ ...editedLocation, isOfficial: e.target.value === "true" })}
                  disabled={!isEditing}
                >
                  <MenuItem value="true">Yes</MenuItem>
                  <MenuItem value="false">No</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth sx={styles.formField}>
                <InputLabel>Official Status</InputLabel>
                <Select
                  value={editedLocation.officialStatus || 'none'}
                  label="Official Status"
                  onChange={(e) => setEditedLocation({ ...editedLocation, officialStatus: e.target.value })}
                  disabled={!isEditing}
                >
                  <MenuItem value="none">None</MenuItem>
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="verified">Verified</MenuItem>
                </Select>
              </FormControl>

              <Box sx={{ mb: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Owner Information
                </Typography>
                {!isEditing ? (
                  <Typography>
                    Owner: {editedLocation.creator?.displayName || 'Unknown'} (ID: {editedLocation.creator?._id || 'N/A'})
                  </Typography>
                ) : (
                  <FormControl fullWidth sx={styles.formField}>
                    <InputLabel>Owner</InputLabel>
                    <Select
                      value={editedLocation.creator?._id || ''}
                      label="Owner"
                      onChange={(e) => {
                        const selectedUser = users.find(user => user._id === e.target.value);
                        setEditedLocation({
                          ...editedLocation,
                          creator: selectedUser
                        });
                      }}
                      disabled={loadingUsers}
                    >
                      {users.map(user => (
                        <MenuItem key={user._id} value={user._id}>
                          {user.displayName} ({user._id})
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
              </Box>
            </>
          )}

          {/* Edit/Save Buttons */}
          <Box sx={styles.buttonGroup}>
            {!isEditing ? (
              <Button
                variant="contained"
                color="primary"
                onClick={() => setIsEditing(true)}
              >
                Edit Details
              </Button>
            ) : (
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  variant="contained"
                  color="primary"
                  onClick={handleSave}
                >
                  Save Changes
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setIsEditing(false);
                    setEditedLocation(location);
                  }}
                >
                  Cancel
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Box>

      <Box sx={styles.mapSection}>
        {/* Map Component */}
        {mapCenter && (
          <Box sx={styles.mapContainer}>
            <MapComponent
              mapCenter={mapCenter}
              mapBounds={location.level === 'Place' ? null : (isEditingBounds ? null : mapBounds)}
              polygonPath={location.level === 'Place' ? [] : polygonPoints}
              cornerMarkers={location.level === 'Place' ? {} : localCornerMarkers}
              handleMarkerDrag={location.level === 'Place' ? handlePlaceLocationChange : undefined}
              onMarkerDragStart={location.level === 'Place' ? undefined : handleMarkerDragStart}
              onMarkerDragEnd={location.level === 'Place' ? undefined : handleMarkerDragEnd}
              locationName={location.name}
              neighborhoodBoundaries={visibleNeighborhoods}
              centerPoint={location.level === 'Place' ? getPlaceLocation() || undefined : undefined}
              onCenterPointDrag={location.level === 'Place' ? handlePlaceLocationChange : undefined}
              isPlace={location.level === 'Place'}
              height={600}
              width={600}
              isAddingPoints={editMode === 'add'}
              onAddPoint={location.level === 'Place' ? undefined : handleAddPoint}
              onDeletePoint={location.level === 'Place' ? undefined : handleDeletePoint}
              onInsertPoint={location.level === 'Place' ? undefined : handleInsertPoint}
              lockCameraOnDrag={true} // Lock camera during bounding box editing
              disableBoundsFitting={isEditingBounds} // Disable bounds fitting during editing
            />
          </Box>
        )}

        {/* Map Controls and Info */}
        {location.level !== 'Place' && (
          <Paper sx={{ p: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
              <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
                <ToggleButtonGroup
                  value={editMode}
                  exclusive
                  onChange={(e, newMode) => newMode && setEditMode(newMode)}
                  size="small"
                >
                  <ToggleButton value="move">
                    <Tooltip title="Move points">
                      <EditIcon />
                    </Tooltip>
                  </ToggleButton>
                  <ToggleButton value="add">
                    <Tooltip title="Add points">
                      <AddLocationIcon />
                    </Tooltip>
                  </ToggleButton>
                </ToggleButtonGroup>
                
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={() => setPolygonPoints([])}
                  disabled={polygonPoints.length === 0}
                >
                  Clear Points
                </Button>
              </Box>
              
              {hasUnsavedChanges && (
                <Button
                  variant="contained"
                  color="primary"
                  onClick={async () => {
                    console.log('Saving boundary with points:', polygonPoints);
                    const closedPoints = [...polygonPoints];
                    if (closedPoints.length >= 3 && 
                        JSON.stringify(closedPoints[0]) !== JSON.stringify(closedPoints[closedPoints.length - 1])) {
                      closedPoints.push({ ...closedPoints[0] });
                    }
                    console.log('Closed polygon points:', closedPoints);
                    await handleSaveBoundingBox(location._id, closedPoints);
                    setHasUnsavedChanges(false);
                  }}
                >
                  Save Boundary
                </Button>
              )}
            </Box>
          </Paper>
        )}

        {/* Bounding Box and Points Info */}
        {(bounds || hasCoordinates || (location.level === 'Place' && location.location?.coordinates)) && (
          <Paper sx={{ p: 2, mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
              {bounds && (
                <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    Bounding Box
                  </Typography>
                  <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <span>North: {bounds.north.toFixed(6)}</span>
                      <span>South: {bounds.south.toFixed(6)}</span>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <span>East: {bounds.east.toFixed(6)}</span>
                      <span>West: {bounds.west.toFixed(6)}</span>
                    </Box>
                  </Box>
                </Box>
              )}

              {(hasCoordinates || (location.level === 'Place' && location.location?.coordinates)) && (
                <Box sx={{ flex: '1 1 300px', minWidth: 0 }}>
                  <Typography variant="subtitle2" gutterBottom>
                    {location.level === 'Place' ? "Location" : "Center Point"}
                  </Typography>
                  <Box sx={{ fontFamily: 'monospace', fontSize: '0.875rem' }}>
                    {location.level === 'Place' ? (
                      <>
                        Latitude: {location.location.coordinates[1].toFixed(6)}<br />
                        Longitude: {location.location.coordinates[0].toFixed(6)}
                      </>
                    ) : (
                      <>
                        Latitude: {location.coordinates.coordinates[1].toFixed(6)}<br />
                        Longitude: {location.coordinates.coordinates[0].toFixed(6)}
                      </>
                    )}
                  </Box>
                  {location.level !== 'Place' && (
                    <Button
                      variant="contained"
                      color="primary"
                      size="small"
                      onClick={() => setShowCenterPointDialog(true)}
                      sx={{ mt: 2 }}
                    >
                      {hasCoordinates ? "Update Center Point" : "Set Center Point"}
                    </Button>
                  )}
                </Box>
              )}
            </Box>

            {/* All Points Display */}
            {location.level !== 'Place' && polygonPoints.length > 0 && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="subtitle2" gutterBottom>
                  All Points ({polygonPoints.length}):
                </Typography>
                <Box sx={{ 
                  maxHeight: '200px', 
                  overflowY: 'auto',
                  fontFamily: 'monospace', 
                  fontSize: '0.875rem' 
                }}>
                  {polygonPoints.map((point, index) => (
                    <Box key={index} sx={{ mb: 0.5 }}>
                      Point {index + 1}: ({point.lat.toFixed(6)}, {point.lng.toFixed(6)})
                    </Box>
                  ))}
                </Box>
              </Box>
            )}
          </Paper>
        )}
      </Box>

      {/* Dialogs */}
      <DisplayJSON
        open={showJsonDialog}
        onClose={() => setShowJsonDialog(false)}
        data={location}
        title="Full JSON Object"
      />

      <Dialog 
        open={showDeleteDialog} 
        onClose={() => {
          setShowDeleteDialog(false);
          setDeleteError(null);
          setErrorChildren([]);
        }}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Delete Location</DialogTitle>
        <DialogContent>
          <Box sx={{ mb: 2 }}>
            <Typography>
              Are you sure you want to delete {location.name}? This action cannot be undone.
            </Typography>
          </Box>

          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError.split('Found')[0]}
            </Alert>
          )}

          {(location.children?.length > 0 || errorChildren.length > 0) && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle1" sx={{ mb: 1 }}>
                Child Locations
              </Typography>
              <Alert severity="warning" sx={{ mb: 2 }}>
                The following child locations must be deleted first:
              </Alert>
              <List>
                {(errorChildren.length > 0 ? errorChildren : location.children).map((child: any) => (
                  <ListItem key={child.id || child._id} divider>
                    <ListItemText 
                      primary={`${child.name} (${child.level})`}
                      secondary={`ID: ${child.id || child._id}`}
                    />
                    <ListItemSecondaryAction>
                      {deletingChildren[child.id || child._id] ? (
                        <CircularProgress size={24} />
                      ) : (
                        <IconButton
                          edge="end"
                          aria-label="delete"
                          onClick={() => handleDeleteChild(child.id || child._id, child.name, child.level)}
                          color="error"
                        >
                          <DeleteIcon />
                        </IconButton>
                      )}
                    </ListItemSecondaryAction>
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button 
            onClick={() => {
              setShowDeleteDialog(false);
              setDeleteError(null);
              setErrorChildren([]);
            }} 
            disabled={isDeleting || Object.values(deletingChildren).some(Boolean)}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleDelete} 
            color="error" 
            disabled={isDeleting || ((location.children?.length > 0 || errorChildren.length > 0)) || Object.values(deletingChildren).some(Boolean)}
          >
            {isDeleting ? 'Deleting...' : 'Delete'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
});

LocationDetails.displayName = 'LocationDetails';

export default LocationDetails; 